import { ManualEventType } from "../codegen/graphql";

/**
 * Turns a `QuickActionAssetLookup` result into the props the event wizard needs — or
 * into the reason why the dashboard quick action cannot proceed.
 *
 * Kept free of React/Apollo so the branchy part is unit-testable without a backend
 * (same split as `moveWizardSync.ts`).
 *
 * The only judge is the node's `availableInstalledBaseEvents` — the server-computed
 * event matrix. There is no `canInstall` / `canRemove` field, and "installed" is not a
 * flag either: an asset that is not installed still has a live location, parked
 * directly under its client, and the backend signals that state by offering
 * `ASSET_INSTALL` (and withholding `ASSET_REMOVE`) on that node. `removalDate` plays no
 * part — the index only ever holds live locations. `ASSET_EXCHANGE` follows the remove
 * rules; the matrix only offers it on semiconductor assets, so an industry asset ends up
 * as `notAllowed`.
 *
 * One asset can legitimately hold several live locations, each with its own matrix
 * (e.g. one under a semiconductor chamber, one in a pool). Filtering by the requested
 * event usually leaves exactly one, which is what makes those cases actionable instead
 * of ambiguous.
 */

export type QuickActionEventType =
    | ManualEventType.ASSET_INSTALL
    | ManualEventType.ASSET_REMOVE
    | ManualEventType.ASSET_EXCHANGE;

export interface QuickActionSearchHit {
    asset?: { id: string } | null;
    assetLocation?: {
        id: string;
        installedBaseNode?: {
            id: string;
            availableInstalledBaseEvents?: ManualEventType[] | null;
        } | null;
    } | null;
}

export type QuickActionTarget =
    /** No asset in the installed base carries this serial number. */
    | { kind: 'notFound' }
    /** Several locations qualify (or the result set was truncated) — let the user pick in the asset search. */
    | { kind: 'ambiguous' }
    /** Found, but the event matrix rules the action out. */
    | { kind: 'blocked'; reason: 'notInstalled' | 'alreadyInstalled' | 'notAllowed' }
    /** Ready to hand to `CreateInstalledBaseEventModal`. */
    | {
          kind: 'ready';
          assetId: string;
          assetLocationId: string;
          /** The asset's own ASSET-typed node; the wizard resolves the event target from it. */
          installedBaseNodeId: string;
      };

interface UsableHit {
    assetId: string;
    assetLocationId: string;
    installedBaseNodeId: string;
    availableEvents: ManualEventType[];
}

const toUsableHit = (hit: QuickActionSearchHit | null): UsableHit | null => {
    const assetId = hit?.asset?.id;
    const assetLocationId = hit?.assetLocation?.id;
    const node = hit?.assetLocation?.installedBaseNode;
    if (!assetId || !assetLocationId || !node?.id) return null;
    return {
        assetId,
        assetLocationId,
        installedBaseNodeId: node.id,
        availableEvents: node.availableInstalledBaseEvents ?? [],
    };
};

export const resolveQuickActionTarget = (
    hits: (QuickActionSearchHit | null)[] | null | undefined,
    total: number,
    eventType: QuickActionEventType,
): QuickActionTarget => {
    const received = hits ?? [];
    const usable = received
        .map(toUsableHit)
        .filter((hit): hit is UsableHit => !!hit);

    if (usable.length === 0) return { kind: 'notFound' };

    // More index entries exist than we fetched — we cannot know whether the page we
    // got holds the right one, so send the user to the asset search rather than act
    // on a partial result.
    if (total > received.length) return { kind: 'ambiguous' };

    const candidates = usable.filter((hit) => hit.availableEvents.includes(eventType));

    if (candidates.length === 1) {
        const { assetId, assetLocationId, installedBaseNodeId } = candidates[0];
        return { kind: 'ready', assetId, assetLocationId, installedBaseNodeId };
    }
    if (candidates.length > 1) return { kind: 'ambiguous' };

    // Nothing qualifies. The counter-signal in the matrix tells us why, precisely
    // enough to say more than "not allowed".
    const offers = (event: ManualEventType) => usable.some((hit) => hit.availableEvents.includes(event));
    const needsInstalledAsset =
        eventType === ManualEventType.ASSET_REMOVE || eventType === ManualEventType.ASSET_EXCHANGE;
    if (needsInstalledAsset && offers(ManualEventType.ASSET_INSTALL)) {
        return { kind: 'blocked', reason: 'notInstalled' };
    }
    if (eventType === ManualEventType.ASSET_INSTALL && offers(ManualEventType.ASSET_REMOVE)) {
        return { kind: 'blocked', reason: 'alreadyInstalled' };
    }
    return { kind: 'blocked', reason: 'notAllowed' };
};
